"use client";

import { useCallback, useMemo, useState } from "react";
import { HtmlEmailData, ValidationResult } from "@/lib/types/compose-modal-types";

interface UseComposeEmailFormatProps {
  initialFormat?: "plain" | "html";
}

/**
 * Hook for managing email format state
 * Handles plain text vs HTML format and validation
 */
export function useComposeEmailFormat({
  initialFormat = "plain",
}: UseComposeEmailFormatProps = {}) {
  const [emailFormat, setEmailFormat] = useState<"plain" | "html">(initialFormat);
  const [htmlEmailData, setHtmlEmailData] = useState<HtmlEmailData>({
    to: "",
    subject: "",
    body: "",
  });
  const [htmlValidationErrors, setHtmlValidationErrors] = useState<ValidationResult[]>([]);

  const handleEmailFormatChange = useCallback((format: "plain" | "html") => {
    setEmailFormat(format);
  }, []);

  const handleHtmlEmailChange = useCallback((data: Partial<HtmlEmailData>) => {
    setHtmlEmailData((prev) => ({ ...prev, ...data }));
  }, []);

  const handleSetHtmlValidationErrors = useCallback((errors: ValidationResult[]) => {
    setHtmlValidationErrors(errors);
  }, []);

  return useMemo(
    () => ({
      emailFormat,
      htmlEmailData,
      htmlValidationErrors,
      setEmailFormat,
      setHtmlEmailData,
      setHtmlValidationErrors,
      handleEmailFormatChange,
      handleHtmlEmailChange,
      handleSetHtmlValidationErrors,
    }),
    [
      emailFormat,
      htmlEmailData,
      htmlValidationErrors,
      handleEmailFormatChange,
      handleHtmlEmailChange,
      handleSetHtmlValidationErrors,
    ]
  );
}
